/**
 * The change record an `invoice.updated` event carries, in words.
 *
 * The API writes `{ reason, changes: { fields, lines } }` - what each field
 * said before and after, which lines were added, removed or changed. This
 * turns it into the sentence a person reads: "total 10,500.01 → 10,500.00 ·
 * tax 1,601.70 → 1,601.69 · line changed: Design (qty 1 → 2)". Used by the
 * Activity page and the invoice's own history panel, so the two agree.
 */

export interface InvoiceChangeRecord {
  fields?: Record<string, { from: string | null; to: string | null }>;
  lines?: {
    added?: string[];
    removed?: string[];
    changed?: Array<{
      name: string;
      from: { quantity: string; unitRate: string; total: string | null };
      to: { quantity: string; unitRate: string; total: string | null };
    }>;
  };
}

const FIELD_LABELS: Record<string, string> = {
  customer: 'customer',
  date: 'date',
  dueDate: 'due date',
  reference: 'reference',
  currency: 'currency',
  exchangeRate: 'exchange rate',
  placeOfSupply: 'place of supply',
  subtotal: 'subtotal',
  tax: 'tax',
  total: 'total',
  balanceDue: 'balance due',
  project: 'project',
  incomeCategory: 'income category',
  notes: 'notes',
  terms: 'terms',
};

const show = (v: string | null) => (v === null || v === '' ? 'none' : v);
const qty = (q: string) => String(Number(q));

/** Each change as its own phrase, in a stable order: figures first, then lines. */
export function describeInvoiceChanges(changes: InvoiceChangeRecord | null | undefined): string[] {
  if (!changes) return [];
  const out: string[] = [];
  for (const [key, change] of Object.entries(changes.fields ?? {})) {
    const label = FIELD_LABELS[key] ?? key;
    if (change.from === '(changed)') out.push(`${label} changed`);
    else out.push(`${label} ${show(change.from)} → ${show(change.to)}`);
  }
  for (const line of changes.lines?.changed ?? []) {
    const parts: string[] = [];
    if (Number(line.from.quantity) !== Number(line.to.quantity)) parts.push(`qty ${qty(line.from.quantity)} → ${qty(line.to.quantity)}`);
    if (Number(line.from.unitRate) !== Number(line.to.unitRate)) parts.push(`rate ${line.from.unitRate} → ${line.to.unitRate}`);
    if (parts.length === 0 && line.from.total !== line.to.total) parts.push(`amount ${show(line.from.total)} → ${show(line.to.total)}`);
    out.push(`line changed: ${line.name}${parts.length ? ` (${parts.join(', ')})` : ''}`);
  }
  for (const name of changes.lines?.added ?? []) out.push(`line added: ${name}`);
  for (const name of changes.lines?.removed ?? []) out.push(`line removed: ${name}`);
  return out;
}

/**
 * An audit event's metadata as one readable line. Knows the invoice change
 * record; anything else is `key: value`. `via` is left to the caller, which
 * renders it beside the actor.
 */
export function describeMetadata(metadata: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(metadata)) {
    if (key === 'via' || value === null || value === undefined || value === '') continue;
    if (key === 'changes' && typeof value === 'object') {
      parts.push(...describeInvoiceChanges(value as InvoiceChangeRecord));
      continue;
    }
    if (key === 'reason') {
      parts.push(`reason: “${String(value)}”`);
      continue;
    }
    if (Array.isArray(value)) parts.push(`${key}: ${value.map((v) => (typeof v === 'object' && v ? JSON.stringify(v) : String(v))).join(', ')}`);
    else if (typeof value === 'object') parts.push(`${key}: ${JSON.stringify(value)}`);
    else parts.push(`${key}: ${String(value)}`);
  }
  return parts.join(' · ');
}
